import { Autocomplete, Checkbox, TextField , Chip} from "@mui/material";
import React, { useEffect, useState, useRef, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import {
  addDashBoardWidgetAccess,
  getWidgetList,
  getDashBoardWidgetUserList,
  deleteWidgetAccess,
} from "../../../services/dashboardService";
import { SuccessToast } from "../../../CustomHooks/SuccessToast";
import { ErrorToast } from "../../../CustomHooks/ErrorToast";
import { CircularProgress } from "@mui/material";

const SELECT_ALL_VALUE = "Select All";

function arraysEqual(a = [], b = []) {
  if (a.length !== b.length) return false;
  const aSorted = [...a].sort();
  const bSorted = [...b].sort();
  return aSorted.every((val, idx) => val === bSorted[idx]);
}

const DashboardSetting = () => {
  const [widgetListLoading, setWidgetListLoading] = useState(true);
  const [widgetListError, setWidgetListError] = useState(null);
  const [widgetUserListError, setWidgetUserListError] = useState(null);
  const [widgetList, setWidgetList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [data, setData] = useState([]);
  const [changedRows, setChangedRows] = useState({}); // Track changed rows

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState([]);

  const user = JSON.parse(localStorage.getItem("user"));
  const UserID = user?.Id;

  // Store initial user selection for each widget row
  const initialUserSelectionRef = useRef({});

  // Helper to refresh user list and table data after save
  const refreshUserList = async (widgetID) => {
    try {
      const widgetIds = [widgetID];
      const res = await getDashBoardWidgetUserList({ UserID: user?.Id, WidgetID: widgetIds });
      if (res && res.Status === "success" && Array.isArray(res.Data)) {
        // Build userList (unique users from response)
        const allUsersMap = {};
        res.Data.forEach((item) => {
          if (!allUsersMap[item.Id]) {
            allUsersMap[item.Id] = {
              Id: item.Id,
              FullName: item.UserName,
            };
          }
        });
        const allUsers = Object.values(allUsersMap);
        setUserList(allUsers);

        // Segregate data by widgetID and set selected users
        const widgetUserMap = {};
        res.Data.forEach((item) => {
          if (!widgetUserMap[item.WidgetID])
            widgetUserMap[item.WidgetID] = [];
          if (item.UseFlag === 1)
            widgetUserMap[item.WidgetID].push(item.Id);
        });

        // Only update the data for the specific widgetID
        setData((prevData) =>
          prevData.map((row) =>
            row.WidgetID === widgetID
              ? { ...row, Username: widgetUserMap[widgetID] || [] }
              : row
          )
        );

        // Update initial selection for change detection for this widgetID only
        initialUserSelectionRef.current[widgetID] = [...(widgetUserMap[widgetID] || [])];
        setChangedRows((prev) => ({
          ...prev,
          [widgetID]: false,
        }));
      }
    } catch (err) {
      ErrorToast("Error refreshing widget user list.");
    }
  };
console.log("UserList",userList);
  useEffect(() => {
    getWidgetList({ UserID })
      .then((widgetRes) => {
        if (
          widgetRes &&
          widgetRes.Status === "success" &&
          Array.isArray(widgetRes.Data)
        ) {
          setWidgetList(widgetRes.Data);

          const widgetIds = widgetRes.Data.map((w) => w.WidgetID);

          getDashBoardWidgetUserList({ UserID: user?.Id, WidgetID: widgetIds })
            .then((res) => {
              if (res && res.Status === "success" && Array.isArray(res.Data)) {
                setWidgetUserListError(null);
                // Build userList (unique users from response)
                const allUsersMap = {};
                res.Data.forEach((item) => {
                  if (!allUsersMap[item.Id]) {
                    allUsersMap[item.Id] = {
                      Id: item.Id,
                      FullName: item.UserName,
                    };
                  }
                });
                const allUsers = Object.values(allUsersMap);
                setUserList(allUsers);

                // Segregate data by widgetID and set selected users
                const widgetUserMap = {};
                res.Data.forEach((item) => {
                  if (!widgetUserMap[item.WidgetID])
                    widgetUserMap[item.WidgetID] = [];
                  if (item.UseFlag === 1)
                    widgetUserMap[item.WidgetID].push(item.Id);
                });

                const tableData = widgetRes.Data.map((row) => ({
                  ...row,
                  Username: widgetUserMap[row.WidgetID] || [],
                }));

                // Store initial selection for change detection (by WidgetID)
                const initialSelection = {};
                tableData.forEach((row) => {
                  initialSelection[row.WidgetID] = [...row.Username];
                });
                initialUserSelectionRef.current = initialSelection;
                setChangedRows({}); // Reset changed rows on load

                setData(tableData);
              } else {
                setWidgetUserListError(res.Message);
                setUserList([]);
                const tableData = widgetRes.Data.map((row) => ({
                  ...row,
                  Username: [],
                }));
                initialUserSelectionRef.current = {};
                setChangedRows({});
                setData(tableData);
              }
              setWidgetListLoading(false);
            })
            .catch((err) => {
              setWidgetUserListError("Error loading widget user list.");
              setWidgetListLoading(false);
              setUserList([]);
              const tableData = widgetRes.Data.map((row) => ({
                ...row,
                Username: [],
              }));
              initialUserSelectionRef.current = {};
              setChangedRows({});
              setData(tableData);
              console.log("Error in Dashboard component: ", err);
            });
        } else {
          setWidgetListError("Failed to load widgets.");
          setData([]);
          setWidgetListLoading(false);
        }
      })
      .catch((err) => {
        setWidgetListError("Error loading widgets.");
        setWidgetListLoading(false);
        setData([]);
        console.log("Error in Dashboard component: ", err);
      });
  }, []);

  // MultiSelect change handler for User List column
  const handleMultiSelectChange = (selected, widgetID, reason, details) => {
     const selectedValues = (selected || []).map((opt) => opt.value);
    const allUserValues = userList.map((user) => user.Id);
    let finalValues = selectedValues.filter((val) => val !== SELECT_ALL_VALUE);

    // If Select All was involved, use the reason/details to decide action
    if (reason === "selectOption" && selectedValues.includes(SELECT_ALL_VALUE)) {
      // user clicked Select All -> select all users
      finalValues = allUserValues;
    } else if (reason === "removeOption" && details?.option?.value === SELECT_ALL_VALUE) {
      // user removed Select All -> clear all users
      finalValues = [];
    }
    // else: user selected/removed individual option -> finalValues already reflects that

    // Update data state by WidgetID
    const updatedData = data.map((row) =>
      row.WidgetID === widgetID ? { ...row, Username: finalValues } : row
    );
    setData(updatedData);

    // Check if changed from initial selection (by WidgetID)
    const initial = initialUserSelectionRef.current[widgetID] || [];
    const isChanged = !arraysEqual(finalValues, initial);
    setChangedRows((prev) => ({
      ...prev,
      [widgetID]: isChanged,
    }));
  };

  const removeAccess = () =>{
    getDashBoardWidgetUserList().then((res)=>{
      if(res?.Data){
        SuccessToast("Widget access saved successfully!");
      }
    }).catch((e)=>{
      ErrorToast("Error saving widget access.");
    })
  }

  // Table columns including User List column
  const getDashboardSettingsColumns = (widgetList) => {
    const excludeColumns = [
      "WidgetID",
      "Datapoints",
      "WidgetColor",
      "ColorSets",
      "DateRange",
    ];
    const columnKeys =
      widgetList?.length > 0
        ? Object.keys(widgetList[0]).filter(
            (key) => !excludeColumns.includes(key)
          )
        : [];

    const columns = columnKeys.map((key) => {
      const accessorKey = key.replace(/\s+/g, "");
      let filterSelectOptions;
      let header = key;
      let size;
      if (key === "WidgetName") {
        header = "Widget Name";
        size = 220;
      }
      if (key === "WidgetType") {
        header = "Widget Type";
        size = 220;
      }
      if (key === "Description") {
        header = "Description";
        size = 450;
      }
      return {
        accessorKey,
        header,
        size,
        filterVariant: filterSelectOptions ? "multi-select" : "text",
        filterSelectOptions,
        filterFn: filterSelectOptions
          ? "arrIncludesSome"
          : (row, columnId, filterValue) => {
              const cellValue = row.getValue(columnId);
              return (cellValue ?? "")
                .toString()
                .toLowerCase()
                .includes(filterValue.toLowerCase());
            },
      };
    });

    // Add User List column at the end
    columns.push({
      accessorKey: "UserList",
      header: "User List",
      size: 450,
      enableColumnFilter: false,
      filterVariant: undefined,
      filterSelectOptions: undefined,
      filterFn: undefined,
      Cell: ({ row }) => {
        if (widgetUserListError) {
          return (
            <span style={{ fontSize: 13 }}>
               {widgetUserListError}
               
            </span>
          );
        }
        const widgetID = row.original?.WidgetID;
        const multiSelectOptions = [
          { label: SELECT_ALL_VALUE, value: SELECT_ALL_VALUE },
          ...userList.map((user) => ({
            label: user.FullName,
            value: user.Id,
          })),
        ];
        const selectedValues = row.original?.Username || [];
        const allUserValues = userList.map((user) => user.Id);
        const isAllSelected =
          allUserValues.length > 0 &&
          selectedValues.length === allUserValues.length;

        return (
           <Autocomplete
            multiple
            disableCloseOnSelect
            className="UserMultiSelectAutoComplete"
            options={multiSelectOptions}
            limitTags={2}
            value={
              isAllSelected
                ? multiSelectOptions // show every option selected including Select All
                : multiSelectOptions.filter((option) =>
                    selectedValues.includes(option.value)
                  )
            }
            onChange={(_, newValue, reason, details) =>
              handleMultiSelectChange(newValue, widgetID, reason, details)
            }
            getOptionLabel={(option) => option.label}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox style={{ marginRight: 2 }} checked={selected} />
                {option.label}
              </li>
            )}
            // custom tag rendering: hide Select All chip, show compact "All (N)" or up-to-2 chips + +N
            renderTags={(value, getTagProps) => {
              const selectedOptions = value || [];
              const withoutSelectAll = selectedOptions.filter(o => o.value !== SELECT_ALL_VALUE);

              // If Select All is present, show a single compact chip
              if (selectedOptions.some(o => o.value === SELECT_ALL_VALUE)) {
                return (
                  <Chip
                    label={`All Selected (${withoutSelectAll.length})`}
                    size="small"
                    {...getTagProps({ index: 0 })}
                    sx={{ mr: 0.5 }}
                  />
                );
              }

              // Otherwise render up to 2 chips and a "+N" indicator
              const chips = withoutSelectAll.slice(0, 2).map((option, index) => (
                <Chip
                  key={option.value}
                  label={option.label}
                  size="small"
                  {...getTagProps({ index })}
                  sx={{ mr: 0.5 }}
                />
              ));

              if (withoutSelectAll.length > 2) {
                chips.push(
                  <span key="more" style={{ marginLeft: 6, fontSize: 12 }}>
                    +{withoutSelectAll.length - 2}
                  </span>
                );
              }

              return chips;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                placeholder="Select Users"
              />
            )}
            sx={{ width: "100%", zIndex: 10 }}
          />
        );
      },
    });

    columns.push({
      accessorKey: "actions",
      header: "Actions",
      size: 200,
      enableColumnFilter: false,
      filterVariant: undefined,
      filterSelectOptions: undefined,
      filterFn: undefined,
      Cell: ({ row }) => {
        const widgetID = row.original?.WidgetID;
        const userIds = row.original?.Username || [];
        const initialUserIds = initialUserSelectionRef.current[widgetID] || [];

        // Use value-based array comparison
        const isChanged = !arraysEqual(userIds, initialUserIds);

        const handleRowSave = async () => {
          if (!isChanged) return;
          try {
            const payload = {
              UserID,
              WidgetID: widgetID,
              AccessList: userIds,
            };
            if (userIds.length === 0) {
              const payload = {
                UserID,
                widgetID: widgetID,
              };
              deleteWidgetAccess(payload)
                .then(async (res) => {
                  if (res?.Data) {
                    SuccessToast("Widget access saved successfully!");
                    await refreshUserList(widgetID);
                    // setChangedRows((prev) => ({
                    //   ...prev,
                    //   [widgetID]: false,
                    // }));
                  } else {
                    //ErrorToast("Error saving widget access.");
                    await refreshUserList(widgetID);
                  }
                })
                .catch((e) => {
                  ErrorToast("Error saving widget access.");
                });
              return;
            }
            const res = await addDashBoardWidgetAccess(payload);
            if (res && res.Status === "success") {
              SuccessToast("Widget access saved successfully!");
              await refreshUserList(widgetID);
              setChangedRows((prev) => ({
                ...prev,
                [widgetID]: false,
              }));
            } else {
              ErrorToast("Failed to save widget access.");
            }
          } catch (err) {
            ErrorToast("Error saving widget access.");
            console.error(err);
          }
        };

        return (
          <button
            className="DataAccessEdit"
            style={{ width: "80px", padding: "4px 0", fontSize: "14px" }}
            disabled={!isChanged}
            onClick={handleRowSave}
          >
            Save
          </button>
        );
      },
    });

    return columns;
  };

  const filteredData = useMemo(() => {
    let filtered = [...data];

    // Apply global filter
    if (globalFilter) {
      filtered = filtered.filter((row) => {
        return Object.values(row).some((value) =>
          String(value).toLowerCase().includes(globalFilter.toLowerCase())
        );
      });
    }

    // Apply column filters
    columnFilters.forEach((filter) => {
      if (filter.value) {
        filtered = filtered.filter((row) => {
          const cellValue = row[filter.id];
          return String(cellValue).toLowerCase().includes(String(filter.value).toLowerCase());
        });
      }
    });

    return filtered;
  }, [data, globalFilter, columnFilters]);

  const pagedData = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;
    return filteredData.slice(start, end);
  }, [filteredData, pagination]);

  // Reset pagination when filters change
  const handleGlobalFilterChange = (filter) => {
    setGlobalFilter(filter);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleColumnFiltersChange = (filters) => {
    setColumnFilters(filters);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const columns = getDashboardSettingsColumns(widgetList);

  return (
    <div className="SiteMainContainer UserHgtContainer">
      <div className="SiteMainHeightDiv">
        <div
          className="col-lg-12 TabulerReportMainDiv MatReactTableDiv"
          style={{ height: "calc(100vh - 155px)" }}
        >
          <div className="card-body p-0 DashboardSettingTable">
            {widgetListLoading ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "40vh",
                  width: "100%",
                }}
              >
                <CircularProgress />
                <div
                  style={{
                    marginTop: "1rem",
                    fontWeight: "bold",
                    textAlign: "center",
                  }}
                  className="loading"
                >
                  Loading Dashboard Widgets, please wait...
                </div>
              </div>
            ) : data && data.length > 0 ? (
              <MaterialReactTable
                columns={columns}
                data={pagedData}
                enableGlobalFilter={true}
                enableStickyHeader
                enableColumnResizing={true}
                globalFilterFn="contains"
                columnResizeMode="onEnd"
                enableColumnOrdering={true}
                initialState={{ showColumnFilters: false }}
                manualPagination={true}
                manualFiltering={true}
                manualGlobalFilter={true}
                state={{
                  pagination: pagination,
                  globalFilter: globalFilter,
                  columnFilters: columnFilters,
                }}
                onPaginationChange={setPagination}
                onGlobalFilterChange={handleGlobalFilterChange}
                onColumnFiltersChange={handleColumnFiltersChange}
                rowCount={filteredData.length}
                muiTableContainerProps={{
                  className: "MaterialTableJs",
                  sx: {
                    maxHeight: "100%",
                    overflow: "auto",
                  },
                }}
                muiTableHeadProps={{
                  className: "myHeader",
                  sx: {
                    display: data.length === 0 ? "none" : "table-header-group",
                  },
                }}
              />
            ) : widgetListError ? (
              <div
                style={{ color: "red", textAlign: "center", marginTop: "2rem" }}
              >
                {widgetListError}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardSetting;